import React, { useEffect, useRef, useState } from "react";
import { Volume2, ArrowRight } from "lucide-react";
import Logo from "./Logo";

/**
 * First-landing splash: a wall of sewing machines running on the factory
 * floor. Browsers refuse to play sound before the visitor interacts, so the
 * "Start the machines" tap is the moment — the floor clatters to life for a
 * couple of seconds, then the curtain lifts onto the site. "Skip" lifts it
 * silently, and the splash auto-lifts after AUTO_MS if nobody taps. Shown
 * once per browser session.
 */
const SESSION_KEY = "kse_splash_seen";
const AUTO_MS = 7000;
const RUN_MS = 2600;
const MACHINES = 12;

// ---- Sewing-floor sound: several machines, each a ticking noise burst
// ---- at its own rate, panned across the stereo field. Pure Web Audio.
function startFactorySound(durationMs) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return () => {};
  const ctx = new Ctx();
  const now = ctx.currentTime;

  const noiseLen = ctx.sampleRate * 2;
  const buffer = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < noiseLen; i++) data[i] = Math.random() * 2 - 1;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.9, now + 0.35);
  master.gain.setValueAtTime(0.9, now + durationMs / 1000 - 0.7);
  master.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
  master.connect(ctx.destination);

  const voices = [];
  const N = 7;
  for (let v = 0; v < N; v++) {
    const rate = 12 + Math.random() * 7;           // stitches per second
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.playbackRate.value = 0.8 + Math.random() * 0.5;

    // The needle "tick": bright, short
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 1800 + Math.random() * 2200;
    band.Q.value = 0.9;

    // The mechanism "thud": low, underneath
    const low = ctx.createBiquadFilter();
    low.type = "lowpass";
    low.frequency.value = 180 + Math.random() * 120;

    const tick = ctx.createGain();
    tick.gain.value = 0;
    const thud = ctx.createGain();
    thud.gain.value = 0;

    // Square LFO chops the noise into stitches
    const lfo = ctx.createOscillator();
    lfo.type = "square";
    lfo.frequency.value = rate;
    const lfoTick = ctx.createGain();
    lfoTick.gain.value = 0.12;
    const lfoThud = ctx.createGain();
    lfoThud.gain.value = 0.35;
    lfo.connect(lfoTick).connect(tick.gain);
    lfo.connect(lfoThud).connect(thud.gain);

    const pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    if (pan) pan.pan.value = -0.9 + (1.8 * v) / (N - 1);

    src.connect(band).connect(tick);
    src.connect(low).connect(thud);
    const out = pan || master;
    tick.connect(out);
    thud.connect(out);
    if (pan) pan.connect(master);

    // Machines start one after another, like a shift beginning
    const t0 = now + v * 0.12;
    src.start(t0);
    lfo.start(t0);
    voices.push({ src, lfo });
  }

  const stopAt = now + durationMs / 1000 + 0.05;
  voices.forEach(({ src, lfo }) => { src.stop(stopAt); lfo.stop(stopAt); });
  const closeTimer = setTimeout(() => ctx.close().catch(() => {}), durationMs + 300);
  return () => { clearTimeout(closeTimer); ctx.close().catch(() => {}); };
}

function Machine({ i, running }) {
  const dur = (0.11 + (i % 5) * 0.012).toFixed(3);
  const delay = ((i * 0.037) % 0.2).toFixed(3);
  return (
    <svg
      viewBox="0 0 120 90"
      className={`splash-machine ${running ? "is-running" : ""}`}
      style={{ "--needle-dur": `${dur}s`, "--needle-delay": `${delay}s`, "--idle-delay": `${(i * 0.23) % 2}s` }}
      aria-hidden="true"
    >
      {/* base plate */}
      <rect x="6" y="66" width="108" height="10" rx="3" fill="currentColor" opacity="0.9" />
      {/* pillar + arm */}
      <path d="M88 66 V22 a8 8 0 0 0 -8 -8 H34 a8 8 0 0 0 -8 8 v10" fill="none" stroke="currentColor" strokeWidth="9" strokeLinecap="round" />
      {/* head */}
      <rect x="20" y="28" width="22" height="24" rx="4" fill="currentColor" />
      {/* hand wheel */}
      <g className="splash-wheel" style={{ transformOrigin: "100px 34px" }}>
        <circle cx="100" cy="34" r="11" fill="none" stroke="currentColor" strokeWidth="4" />
        <line x1="100" y1="23" x2="100" y2="45" stroke="currentColor" strokeWidth="2" />
        <line x1="89" y1="34" x2="111" y2="34" stroke="currentColor" strokeWidth="2" />
      </g>
      {/* spool */}
      <rect x="44" y="4" width="6" height="12" rx="1" fill="currentColor" opacity="0.8" />
      {/* needle bar + needle */}
      <g className="splash-needle">
        <rect x="28" y="50" width="6" height="10" fill="currentColor" />
        <line x1="31" y1="60" x2="31" y2="70" stroke="currentColor" strokeWidth="1.6" />
      </g>
      {/* presser foot */}
      <rect x="25" y="62" width="12" height="3" rx="1" fill="currentColor" opacity="0.7" />
      {/* fabric + stitches */}
      <rect x="6" y="62" width="60" height="4" fill="#c7d0ff" opacity="0.35" />
      <line className="splash-stitch" x1="10" y1="64" x2="60" y2="64" stroke="#ffffff" strokeWidth="1.2" strokeDasharray="3 3" />
    </svg>
  );
}

export default function SplashIntro() {
  const [phase, setPhase] = useState(() => {
    // Skip for visitors sent to a specific section (a #hash deep link) or
    // with ?nosplash, and for anyone who has already seen it this session.
    try {
      if (window.location.hash || /[?&]nosplash/.test(window.location.search)) return "done";
      return sessionStorage.getItem(SESSION_KEY) ? "done" : "idle";
    } catch {
      return "idle";
    }
  });
  const stopSound = useRef(null);
  const timers = useRef([]);

  const finish = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
    setPhase("leaving");
    timers.current.push(setTimeout(() => setPhase("done"), 900));
  };

  const startMachines = () => {
    if (phase !== "idle") return;
    setPhase("running");
    try { stopSound.current = startFactorySound(RUN_MS + 400); } catch {}
    timers.current.push(setTimeout(finish, RUN_MS));
  };

  useEffect(() => {
    if (phase !== "idle") return undefined;
    const t = setTimeout(finish, AUTO_MS);
    timers.current.push(t);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [phase]);

  useEffect(() => {
    if (phase === "done" || phase === "leaving") {
      document.body.style.overflow = "";
    } else {
      document.body.style.overflow = "hidden";
    }
    return () => { document.body.style.overflow = ""; };
  }, [phase]);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    stopSound.current?.();
  }, []);

  if (phase === "done") return null;
  const running = phase === "running";

  return (
    <div
      className={`splash fixed inset-0 z-[100] bg-[#0d1640] text-white flex flex-col ${phase === "leaving" ? "splash-leave" : ""}`}
      role="dialog"
      aria-label="Welcome to K.S.E. Inc."
    >
      {/* the floor */}
      <div className="splash-floor absolute inset-0 grid grid-cols-3 sm:grid-cols-4 gap-x-6 gap-y-4 px-6 py-24 sm:px-16 items-center content-center pointer-events-none text-blue-100/70">
        {Array.from({ length: MACHINES }).map((_, i) => (
          <Machine key={i} i={i} running={running} />
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0d1640]/70 via-transparent to-[#0d1640]" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse 42% 38% at 50% 48%, rgba(13,22,64,0.95) 0%, rgba(13,22,64,0.75) 55%, rgba(13,22,64,0) 100%)" }} />

      {/* the message */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6">
        <div className={running ? "splash-logo-run" : ""}>
          <Logo size="xl" />
        </div>
        <p className="mt-6 text-blue-100/80 text-sm sm:text-base tracking-[0.35em] uppercase">
          {running ? "Shift starting on the floor" : "Mumbai · Umargam · Tiruppur"}
        </p>
        <p className="mt-2 text-blue-100/60 text-sm">
          {running ? "500 seats · 4,00,000 pieces a month" : "Three factories. 500 skilled hands. Since 1998."}
        </p>

        {!running && (
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <button
              type="button"
              onClick={startMachines}
              className="kse-btn bg-white text-[#1a2a6b] px-8 py-4 text-base shadow-2xl splash-cta"
            >
              <Volume2 size={18} /> Start the machines
            </button>
            <button type="button" onClick={finish} className="text-blue-100/70 hover:text-white text-sm inline-flex items-center gap-1">
              Skip <ArrowRight size={14} />
            </button>
          </div>
        )}
      </div>

      <div className="relative z-10 pb-6 text-center text-[11px] text-blue-100/40 tracking-[0.25em] uppercase">
        {running ? "♪ machines running ♪" : "tap to hear the floor"}
      </div>
      {!running && <div className="splash-progress absolute bottom-0 left-0 h-[3px] bg-blue-300/70" style={{ animationDuration: `${AUTO_MS}ms` }} />}
    </div>
  );
}
